"use client";

import { cn } from "@/lib/utils";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";

interface ProcessStep {
  title: string;
  description: string;
  icon?: string;
}

interface ProcessStepsProps {
  steps: ProcessStep[];
  variant?: 'default' | 'compact' | 'horizontal';
  numberStyle?: 'circle' | 'square' | 'none';
  className?: string;
}

export default function ProcessSteps({
  steps,
  variant = 'default',
  numberStyle = 'circle',
  className = ''
}: ProcessStepsProps) {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);

  if (variant === 'horizontal') {
    return (
      <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8", className)}>
        {steps.map((step, index) => (
          <div key={index} className="text-center">
            <div className="mb-4 flex justify-center">
              {numberStyle !== 'none' && (
                <div className={cn(
                  "flex h-12 w-12 items-center justify-center text-primary font-bold",
                  numberStyle === 'circle' ? "rounded-full bg-primary-surface" : "rounded-lg bg-primary-surface"
                )}>
                  {step.icon ? (
                    <i className={step.icon}></i>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
              )}
            </div>
            <h4 className="mb-2 text-lg font-semibold text-neutral-emphasis">{translate(step.title)}</h4>
            <p className="text-neutral-muted">{translate(step.description)}</p>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={cn("space-y-4", className)}>
        {steps.map((step, index) => (
          <div key={index} className="flex items-start">
            {numberStyle !== 'none' && (
              <div className={cn(
                "mr-4 mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center text-primary font-bold text-sm",
                numberStyle === 'circle' ? "rounded-full bg-primary-surface" : "rounded bg-primary-surface"
              )}>
                {step.icon ? (
                  <i className={step.icon}></i>
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
            )}
            <div>
              <h5 className="mb-1 font-semibold text-neutral-emphasis">{translate(step.title)}</h5>
              <p className="text-neutral-muted text-sm">{translate(step.description)}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default variant - similar to ServiceStepList but more general
  return (
    <div className={cn("bg-card p-6 rounded-lg shadow-md", className)}>
      <ol className="space-y-4">
        {steps.map((step, index) => (
          <li key={index} className="flex items-start">
            {numberStyle !== 'none' && (
              <div className={cn(
                "mr-8 mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center text-primary font-bold",
                numberStyle === 'circle' ? "rounded-full bg-primary-surface" : "rounded-lg bg-primary-surface"
              )}>
                {step.icon ? (
                  <i className={step.icon}></i>
                ) : (
                  <span className="text-sm">{index + 1}</span>
                )}
              </div>
            )}
            <div>
              <h5 className="mb-1 font-semibold text-neutral-emphasis">{translate(step.title)}</h5>
              <p className="text-neutral-muted">{translate(step.description)}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
